import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { is } from 'ramda';
import classNames from 'classnames/bind';
import { Link } from 'react-router-dom';

import Icon from 'components/common/Icon/Icon';
import ToolTip from 'components/common/ToolTip/ToolTip';
import SolidSpinner from 'components/loading/SolidSpinner/SolidSpinner';
import { ButtonTypes, SpinnerTypes } from 'constants/Constants';

import styles from './Button.module.scss';

const { PRIMARY, NAV, NAV_ACTIVE, SECONDARY, TERTIARY, IGNORE, FLAT, SMALL, MEDIUM, LARGE } = ButtonTypes;

const s = classNames.bind(styles);

const Button = ({
	className,
	id,
	href,
	to,
	target = '_BLANK',
	wrapperElement,
	color,
	flat,
	inverted,
	size,
	text,
	icon,
	withoutTextOnSmall,
	withSpaceBetween,
	imageUrl,
	iconClassName,
	withIconLeft,
	iconSizeSmall,
	rotateIcon,
	skeleton,
	truncateText,
	onClickCallback,
	loading,
	disabled,
	toolTip,
	counter,
	showCounterbar,
	counterbarPercent,
	children,
	withSeparator,
	withSeparatorLeft,
	type,
	style
}) => {
	/**
	 * Button classNames
	 */
	const BUTTON_CLASSES = s('container', {
		// Colors
		container_primary: color === PRIMARY,
		container_secondary: color === SECONDARY && !inverted,
		container_nav: color === NAV,
		container_nav_active: color === NAV_ACTIVE,
		container_secondary_inverted: color === SECONDARY && inverted,
		container_tertiary: color === TERTIARY && !inverted,
		container_tertiary_inverted: color === TERTIARY && inverted,
		container_ignore: color === IGNORE && !inverted,
		// Variants
		container_flat: flat === FLAT,
		// Sizes
		container_small: size === SMALL,
		container_medium: size === MEDIUM,
		container_large: size === LARGE,
		container_counter: counter,
		container_link: to,
		container_truncateText: truncateText,
		// Loading
		container_disabled: disabled,
		container_skeleton: skeleton,
		loading,
		// Separator
		container_withSeparator: withSeparator,
		container_withSeparatorLeft: withSeparatorLeft,
		// Icon only
		iconOnly: !text && icon,
		// Hide text on smaller viewports to save space
		withoutTextOnSmall,
		[className]: className
	});

	/**
	 * Icon classNames
	 */
	const BUTTON_ICON_CLASSES = s('icon', {
		icon_small: size === ButtonTypes.SMALL || iconSizeSmall,
		icon_left: withIconLeft,
		icon_skeleton: skeleton,
		icon_rotate: rotateIcon,
		icon_withoutTextOnSmall: withoutTextOnSmall,
		icon_withoutText: !text,
		[iconClassName]: iconClassName
	});

	/**
	 * image classNames
	 */
	const BUTTON_IMAGE_ICON_CLASSES = s('imageIcon', {
		imageIcon_small: size === ButtonTypes.SMALL || iconSizeSmall,
		imageIcon_left: withIconLeft,
		[iconClassName]: iconClassName
	});

	/**
	|--------------------------------------------------
	| @function getButtonIcon SVG
	|--------------------------------------------------
	*/
	const getButtonIcon = () => {
		if (imageUrl) {
			return <img src={imageUrl} className={BUTTON_IMAGE_ICON_CLASSES} alt={text} />;
		}

		if (React.isValidElement(icon)) {
			return icon;
		}

		if (!icon) {
			return null;
		}

		return <Icon icon={icon} className={BUTTON_ICON_CLASSES} />;
	};

	/**
	|--------------------------------------------------
	| @function getInnerMarkup
	|--------------------------------------------------
	*/
	const getInnerMarkup = () => (
		<div
			className={s('inner', {
				inner_withIconLeft: withIconLeft,
				inner_withSpaceBetween: counter || withSpaceBetween
			})}
		>
			{/**
					|--------------------------------------------------
					| Spinner
					|--------------------------------------------------
					*/
			loading && (
				<div className={s('spinnerContainer')}>
					<SolidSpinner className={s('spinner')} size={SpinnerTypes.SMALL} color={SpinnerTypes.BLUE} />
				</div>
			)}
			{/**
					|--------------------------------------------------
					| Text
					|--------------------------------------------------
					*/
			text && <span className={s('text', { text_withoutTextOnSmall: withoutTextOnSmall, text_truncateText: truncateText })}>{text}</span>}
			<div className={s('rightInner')}>
				{/**
					|--------------------------------------------------
					| Children
					|--------------------------------------------------
					*/

				children && <span className={s('childrenContainer')}>{children}</span>}

				{/**
					|--------------------------------------------------
					| Icon
					|--------------------------------------------------
					*/
				getButtonIcon()}

				{/**
					|--------------------------------------------------
					| Counter bar
					|--------------------------------------------------
					*/
				showCounterbar && (
					<span>
						<div className={s('counterbarContainer')}>
							<div className={s('counterbar')} style={{ height: `${counterbarPercent}%` }} />
						</div>
					</span>
				)}
			</div>
		</div>
	);
	/**
	|--------------------------------------------------
	| @function getWrapperElement
	|--------------------------------------------------
	*/
	const getWrapperElement = () => {
		/**
		 * If it is a link appearing as a button
		 */
		if (href) {
			return (
				<a id={id || null} className={BUTTON_CLASSES} href={href} target={target} rel="noopener noreferrer" style={style}>
					{getInnerMarkup()}
				</a>
			);
		}
		/**
		 * If it is a internal react link appearing as a button
		 */
		if (to) {
			return (
				<Link className={BUTTON_CLASSES} to={to} style={style}>
					{getInnerMarkup()}
				</Link>
			);
		}

		if (wrapperElement && wrapperElement === 'div') {
			return (
				<div role="button" id={id || null} className={BUTTON_CLASSES} onClick={!loading ? onClickCallback : () => null} style={style}>
					{getInnerMarkup()}
				</div>
			);
		}
		/**
		 * If button is of type button
		 */
		return (
			<button
				id={id || null}
				className={BUTTON_CLASSES}
				disabled={loading || skeleton}
				onClick={!loading ? onClickCallback : () => null}
				style={style}
				type={type}
			>
				{getInnerMarkup()}
			</button>
		);
	};

	/**
	|--------------------------------------------------
	| @render element
	|--------------------------------------------------
	*/
	return toolTip ? (
		<ToolTip text={is(String, toolTip) ? toolTip : null} component={is(Object, toolTip) ? toolTip : null} id={id}>
			{getWrapperElement()}
		</ToolTip>
	) : (
		getWrapperElement()
	);
};

Button.defaultProps = {
	wrapperElement: 'button',
	type: 'button'
};

Button.propTypes = {
	className: PropTypes.string,
	style: PropTypes.object,
	wrapperElement: PropTypes.oneOf(['div', 'button']),
	color: PropTypes.oneOf([PRIMARY, SECONDARY, TERTIARY, NAV, NAV_ACTIVE, IGNORE]),
	disabled: PropTypes.bool,
	href: PropTypes.string,
	icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	iconClassName: PropTypes.string,
	withIconLeft: PropTypes.bool,
	iconSizeSmall: PropTypes.bool,
	id: PropTypes.string,
	imageUrl: PropTypes.string,
	inverted: PropTypes.bool,
	flat: PropTypes.string,
	loading: PropTypes.bool,
	onClickCallback: PropTypes.func,
	rotateIcon: PropTypes.bool,
	size: PropTypes.oneOf([SMALL, MEDIUM, LARGE]).isRequired,
	skeleton: PropTypes.bool,
	target: PropTypes.string,
	text: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.node]),
	truncateText: PropTypes.bool,
	to: PropTypes.string,
	toolTip: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	withoutTextOnSmall: PropTypes.bool,
	withSpaceBetween: PropTypes.bool,
	withSeparator: PropTypes.bool,
	withSeparatorLeft: PropTypes.bool,
	showCounterbar: PropTypes.bool,
	counterbarPercent: PropTypes.number,
	counter: PropTypes.shape({
		id: PropTypes.number,
		type: PropTypes.string,
		selected: PropTypes.bool,
		subType: PropTypes.string
	}),
	type: PropTypes.string,
	userIcon: PropTypes.number,
	children: PropTypes.node
};

export default memo(Button);
